/* eslint-disable max-statements */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../services/auth-service';

const GoogleAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasProcessed.current) {
      return;
    }

    const handleGoogleCallback = async () => {
      // Mark as processed immediately to prevent race conditions
      hasProcessed.current = true;

      try {
        const code = searchParams.get('code');
        const error = searchParams.get('error');

        if (error) {
          setError('Google authentication was cancelled or failed');
          setLoading(false);
          return;
        }

        if (!code) {
          setError('No authorization code received from Google');
          setLoading(false);
          return;
        }

        // Exchange the authorization code for tokens
        const authResponse = await authService.handleGoogleCallback(code);

        // Check if the callback was successful
        // NOTE: With httpOnly cookies, token is in cookie - we only check for user data
        if (authResponse && authResponse.user) {
          // Dispatch custom event to notify App component of successful login
          const loginEvent = new CustomEvent('googleLoginSuccess', {
            detail: authResponse
          });
          window.dispatchEvent(loginEvent);
          
          // Small delay to ensure event is processed, then navigate
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 100);
          
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 1000);
        } else {
          // Handle failed authentication
          setError('Google authentication failed - please try again');
          setLoading(false);
        }
        
      } catch (error) {
        console.error('Google callback error:', error);
        setError('Failed to complete Google authentication');
        setLoading(false);
      }
    };

    handleGoogleCallback();
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div className="google-callback-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Completing Google authentication...</p>
          <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '1rem' }}>
            You will be redirected automatically in a moment.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="google-callback-container">
        <div className="google-auth-error">
          <h2>Authentication Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="retry-button">
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default GoogleAuthCallback; 